import React, { useState, useMemo, useEffect } from 'react';
import FormInput from '../common/FormInput';
import { useCrudOperations } from '../Hooks/useCrudOperations';
import { validatePostCode, validateProjectName } from '../../utils/validation';
import { API_URL, authHeaders } from '../../utils/api';
import { invalidateGlobalCache } from '../../hooks/useApiCache';

function AddProject({ builders = [], users = [], setProjects, projects, onClose }) {
  // Lägg till state för tab-hantering
  const [activeTab, setActiveTab] = useState('project'); // 'project' eller 'team'
  
  const [values, setValues] = useState({
    projectId: '',
    projectDescription: '',
    projectType: 'Renovering',
    address: '',
    postCode: '',
    city: '',
    propertyDesignation: '',
    customer: '',
    additionalCustomers: [],
    consultant: '',
    architect: '',
    builder: '',
    builderContactPerson: '',
    selectedSubcontractors: [],
    subcontractors: [],
    consultants: [],
    architects: [],
    materialSuppliers: []
  });

  // Lägg till state för kundsökning
  const [mainCustomerSearch, setMainCustomerSearch] = useState('');
  const [showMainCustomerDropdown, setShowMainCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Lägg till state för extra kunder
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [, setErrorMessage] = useState('');

  const { addItem } = useCrudOperations(`${API_URL}/api/projects`, setProjects, projects);

  // Lägg till dessa state-variabler
  const [materialSuppliers, setMaterialSuppliers] = useState([]);
  const [suppliersList, setSuppliersList] = useState([]);
  const [materialSupplierUsers, setMaterialSupplierUsers] = useState([]);
  const [materialSupplierSearchTerm, ] = useState('');
  const [, setFilteredSuppliers] = useState([]);

  // Materialleverantörer: sök och välj person direkt (ingen "lägg till"-knapp)
  const [materialSupplierPersonSearch, setMaterialSupplierPersonSearch] = useState('');
  const [showMaterialSupplierPersonDropdown, setShowMaterialSupplierPersonDropdown] = useState(false);

  // Filtrera kunder baserat på sökning för huvudkund
  const filteredMainCustomers = useMemo(() => {
    return users
      .filter(user => 
        user.role === 'kund' && 
        !values.additionalCustomers.includes(user._id) &&
        (user.firstName.toLowerCase().includes(mainCustomerSearch.toLowerCase()) ||
         user.lastName.toLowerCase().includes(mainCustomerSearch.toLowerCase()))
      )
      .slice(0, 10); // Visa max 10 resultat
  }, [users, mainCustomerSearch, values.additionalCustomers]);

  // Filtrera kunder baserat på sökning för extra kunder
  const filteredCustomers = useMemo(() => {
    return users
      .filter(user => 
        (user.role === 'kund' || user.role === 'extrakund') && 
        user._id !== values.customer &&
        !values.additionalCustomers.includes(user._id) &&
        (
          user.firstName.toLowerCase().includes(customerSearch.toLowerCase()) ||
          user.lastName.toLowerCase().includes(customerSearch.toLowerCase()) ||
          (user.email || '').toLowerCase().includes(customerSearch.toLowerCase())
        )
      )
      .slice(0, 10); // Visa max 10 resultat
  }, [users, customerSearch, values.customer, values.additionalCustomers]);

  // Filtrera byggare som är kopplade till det valda byggföretaget
  const filteredBuilderContacts = useMemo(() => {
    if (!values.builder) return [];
    
    // Hitta den valda byggaren
    const selectedBuilder = builders.find(b => b._id === values.builder);
    
    // Samla kontaktpersoner från båda källor
    const contactPersons = [];
    
    // 1. Från användare som är kopplade till byggaren
    const userContacts = users.filter(user => {
      if (user.role !== 'byggare' && user.role !== 'admin') return false;
      
      // Hantera både fall där builder är ett ID och där det är ett objekt
      if (typeof user.builder === 'string') {
        return user.builder === values.builder;
      } else if (user.builder && user.builder._id) {
        return user.builder._id === values.builder;
      }
      
      return false;
    });
    
    contactPersons.push(...userContacts);
    
    // 2. Från byggföretagets contactPerson och additionalContactPersons fält
    if (selectedBuilder) {
      // Lägg till huvudkontaktperson om den finns och inte redan är tillagd
      if (selectedBuilder.contactPerson && !contactPersons.some(u => u._id === selectedBuilder.contactPerson._id)) {
        const contactUser = users.find(u => u._id === selectedBuilder.contactPerson._id);
        if (contactUser) {
          contactPersons.push(contactUser);
        }
      }
      
      // Lägg till ytterligare kontaktpersoner om de finns
      if (Array.isArray(selectedBuilder.additionalContactPersons)) {
        selectedBuilder.additionalContactPersons.forEach(contactId => {
          // Kontrollera om ID:t är ett objekt eller en sträng
          const contactPersonId = typeof contactId === 'object' ? contactId._id : contactId;
          
          // Lägg till om den inte redan finns
          if (!contactPersons.some(u => u._id === contactPersonId)) {
            const contactUser = users.find(u => u._id === contactPersonId);
            if (contactUser) {
              contactPersons.push(contactUser);
            }
          }
        });
      }
    }
    
    // Om det bara finns en kontaktperson, välj den automatiskt
    if (contactPersons.length === 1 && values.builder && !values.builderContactPerson) {
      // Sätt timeout för att undvika problem med renderingsordning
      setTimeout(() => {
        setValues(prev => ({
          ...prev,
          builderContactPerson: contactPersons[0]._id
        }));
      }, 0);
    }
    
    return contactPersons;
  }, [users, values.builder, builders, values.builderContactPerson]);

  // Kontrollera om en kund är associerad med befintliga projekt
  const isCustomerAssociatedWithProjects = (customerId) => {
    return projects.some(project => 
      project.customer?._id === customerId || 
      (Array.isArray(project.additionalCustomers) && 
       project.additionalCustomers.some(c => c?._id === customerId))
    );
  };

  // Hantera ändringar i formuläret
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Rensa eventuella felmeddelanden för detta fält
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Hantera specifik logik för olika fält
    if (name === 'customer') {
      setValues(prev => ({
        ...prev,
        [name]: value,
        additionalCustomers: [] // Återställ ytterligare kunder när huvudkunden ändras
      }));
    } 
    else if (name === 'builder') {
      setValues(prev => ({
        ...prev,
        [name]: value,
        builderContactPerson: '' // Återställ kontaktperson när byggföretaget ändras
      }));
    }
    else if (name === 'builderContactPerson') {
      setValues(prev => ({
        ...prev,
        [name]: value
      }));
    }
    else if (name === 'additionalCustomers') {
      // För multiselect behöver vi hantera en array av värden
      setValues(prev => ({
        ...prev,
        additionalCustomers: Array.isArray(value) ? value : []
      }));
    } 
    else {
      // Vanlig fältuppdatering
      setValues(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Lägg till useEffect för att hämta leverantörer och materialleverantörsanvändare
  useEffect(() => {
    const fetchMaterialSupplierData = async () => {
      try {
        const [suppliersResponse, usersResponse] = await Promise.all([
          fetch(`${API_URL}/suppliers`, { headers: authHeaders() }),
          fetch(`${API_URL}/suppliers/material-suppliers/users`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            }
          })
        ]);

        if (suppliersResponse.ok && usersResponse.ok) {
          const [suppliersData, usersData] = await Promise.all([
            suppliersResponse.json(),
            usersResponse.json()
          ]);
          
          setSuppliersList(suppliersData);
          setMaterialSupplierUsers(usersData);
        }
      } catch (error) {
        console.error('Fel vid hämtning av leverantörsdata:', error);
      }
    };

    fetchMaterialSupplierData();
  }, []);

  // Lägg till useEffect för att filtrera leverantörer baserat på sökning
  useEffect(() => {
    if (materialSupplierSearchTerm) {
      const filtered = suppliersList.filter(supplier => 
        supplier.companyName.toLowerCase().includes(materialSupplierSearchTerm.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    } else {
      setFilteredSuppliers([]);
    }
  }, [materialSupplierSearchTerm, suppliersList]);





  // Valideringsfunktioner per tab
  const validateProjectInfoTab = () => {
    const validationErrors = {};
    if (!values.projectId) validationErrors.projectId = 'Projektnamn är obligatoriskt';
    if (values.projectId && !validateProjectName(values.projectId)) validationErrors.projectId = 'Projektnamnet är ogiltigt';
    if (!values.address) validationErrors.address = 'Adress är obligatoriskt';
    if (!values.postCode) validationErrors.postCode = 'Postnummer är obligatoriskt';
    if (values.postCode && !validatePostCode(values.postCode)) validationErrors.postCode = 'Postnummer är ogiltigt';
    if (!values.city) validationErrors.city = 'Ort är obligatoriskt';
    if (!values.customer) validationErrors.customer = 'Kund är obligatoriskt';
    return validationErrors;
  };
  const validateTeamTab = () => {
    // Lägg till validering för team-fält om det behövs
    return {};
  };

  // Hantera tabbyte med validering
  const handleTabChange = (tab) => {
    if (activeTab === 'project') {
      const errors = validateProjectInfoTab();
      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        return;
      }
    }
    if (activeTab === 'team') {
      const errors = validateTeamTab();
      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        return;
      }
    }
    setErrors({});
    setActiveTab(tab);
  };

  // Uppdatera handleSubmit för att validera båda tabs
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errorsProject = validateProjectInfoTab();
    const errorsTeam = validateTeamTab();
    const allErrors = { ...errorsProject, ...errorsTeam };
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      if (Object.keys(errorsProject).length > 0) setActiveTab('project');
      else setActiveTab('team');
      return;
    }
    
    // Se till att vi har rätt format på postnummer (utan mellanslag)
    const formattedPostCode = values.postCode.replace(/\s+/g, '');
    
    // Om vi har ett selectedCustomer-objekt men inget customer-ID, använd ID:t från selectedCustomer
    let customerId = values.customer;
    if (!customerId && selectedCustomer && selectedCustomer._id) {
      customerId = selectedCustomer._id;
    }
    
    // Lägg till detta i projektdata
    const formattedMaterialSuppliers = materialSuppliers
      .filter(ms => ms && ms.supplier && ms.supplier._id && ms._id)
      .map(ms => ({
        supplier: ms.supplier._id,
        contactPerson: ms._id
      }));
    
    // Skapa projektobjekt med alla värden och se till att alla värden har rätt format
    // OBS: Använd fältnamnen som backend förväntar sig (projectId, projectDescription, projectType)
    const projectData = {
      projectId: values.projectId.trim(),
      projectDescription: values.projectDescription.trim(),
      projectType: values.projectType,
      address: values.address.trim(),
      postCode: formattedPostCode,
      city: values.city.trim(),
      propertyDesignation: values.propertyDesignation.trim(),
      customer: customerId,
      // Rensa upp tomma ID:n från arrayen
      additionalCustomers: values.additionalCustomers.filter(id => id),
      // Använd null istället för undefined för tomma värden
      consultant: values.consultant || null,
      architect: values.architect || null,
      builder: values.builder || null,
      builderContactPerson: values.builderContactPerson || null,
      subcontractors: values.selectedSubcontractors || [],
      materialSuppliers: formattedMaterialSuppliers
    };

    // Verifiera att obligatoriska fält finns
    if (!projectData.projectId || !projectData.address || !projectData.postCode || !projectData.city || !projectData.customer) {
      const missingFields = [];
      if (!projectData.projectId) missingFields.push('projektnamn');
      if (!projectData.address) missingFields.push('adress');
      if (!projectData.postCode) missingFields.push('postnummer');
      if (!projectData.city) missingFields.push('postort');
      if (!projectData.customer) missingFields.push('kund');
      
      throw new Error(`Alla obligatoriska fält måste fyllas i. Saknas: ${missingFields.join(', ')}`);
    }
    
    setIsLoading(true);
    try {
      await addItem(projectData);
      onClose();
      
      // 🚀 CACHE FIX: Invalidera relevanta caches när nytt projekt skapas
      invalidateGlobalCache([
        'myprojects_projects',          // MyProjects-komponenten
        'myassigned_projects',          // MyAssignedProjects-komponenten
        'all_projects',                 // Alla projekt
        'dashboard_projects'            // Dashboard-komponenten
      ]);
    } catch (error) {
      console.error('Error adding project:', error);
      setErrorMessage('Fel vid skapande av projekt: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Lägg till en click-handler på document för att stänga dropdowns
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search-main')) {
        setShowMainCustomerDropdown(false);
      }
      if (!event.target.closest('.customer-search-additional')) {
        setShowCustomerDropdown(false);
      }
      if (!event.target.closest('.subcontractor-search')) {
        setShowSubcontractorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Funktion för att hantera val av huvudkund
  const handleSelectMainCustomer = (customer) => {
    setValues(prev => ({
      ...prev,
      customer: customer._id
    }));
    setSelectedCustomer(customer);
    setMainCustomerSearch(`${customer.firstName} ${customer.lastName}`);
    setShowMainCustomerDropdown(false);
  };

  // Funktion för att hantera val av extra kund
  const handleSelectAdditionalCustomer = (customer) => {
    setValues(prev => ({
      ...prev,
      additionalCustomers: [...prev.additionalCustomers, customer._id]
    }));
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  // Funktion för att ta bort en extra kund
  const handleRemoveAdditionalCustomer = (customerId) => {
    setValues(prev => ({
      ...prev,
      additionalCustomers: prev.additionalCustomers.filter(id => id !== customerId)
    }));
  };

  // Lägg till state för UE-sökning
  const [subcontractorSearch, setSubcontractorSearch] = useState('');
  const [showSubcontractorDropdown, setShowSubcontractorDropdown] = useState(false);
  
  // Filtrera UE baserat på sökning
  const filteredSubcontractors = useMemo(() => {
    if (!values.subcontractors || !Array.isArray(values.subcontractors)) {
      return [];
    }
    
    return values.subcontractors
      .filter(ue => 
        ue && // Kontrollera att ue är definierad
        !values.selectedSubcontractors.includes(ue._id) &&
        ((ue.firstName && ue.firstName.toLowerCase().includes(subcontractorSearch.toLowerCase())) ||
         (ue.lastName && ue.lastName.toLowerCase().includes(subcontractorSearch.toLowerCase())) ||
         (ue.companyName && ue.companyName.toLowerCase().includes(subcontractorSearch.toLowerCase())))
      ).slice(0, 10);
  }, [values.subcontractors, subcontractorSearch, values.selectedSubcontractors]);

  // Funktion för att hantera val av UE
  const handleSelectSubcontractor = (subcontractor) => {
    setValues(prev => ({
      ...prev,
      selectedSubcontractors: [...prev.selectedSubcontractors, subcontractor._id]
    }));
    setSubcontractorSearch('');
    setShowSubcontractorDropdown(false);
  };

  // Funktion för att ta bort en UE
  const handleRemoveSubcontractor = (subcontractorId) => {
    setValues(prev => ({
      ...prev,
      selectedSubcontractors: prev.selectedSubcontractors.filter(id => id !== subcontractorId)
    }));
  };

  // I useEffect för att ladda data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_URL}/users`, { headers: authHeaders() });
        const data = await response.json();
        
        // Filtrera användare efter roll
        const customers = data.filter(user => user.role === 'kund');
        const consultants = data.filter(user => user.role === 'byggkonsult');
        const architects = data.filter(user => user.role === 'inredningsarkitekt');
        const subcontractorUsers = data.filter(user => user.role === 'UE');
        
        setValues(prev => ({
          ...prev,
          customers: customers || [],
          consultants: consultants || [],
          architects: architects || [],
          subcontractors: subcontractorUsers || [] // Säkerställ att subcontractors aldrig är undefined
        }));
      } catch (error) {
        console.error('Error fetching users:', error);
        // Om något går fel, sätt tomma arrayer för alla listor
        setValues(prev => ({
          ...prev,
          customers: [],
          consultants: [],
          architects: [],
          subcontractors: []
        }));
      }
    };
    
    fetchUsers();
  }, []);

  // Filtrera materialleverantörer baserat på sökning
  const filteredMaterialSupplierPersons = useMemo(() => {
    if (!materialSupplierPersonSearch) return [];
    return materialSupplierUsers.filter(user =>
      user.role === 'materialleverantör' &&
      !materialSuppliers.some(ms => ms._id === user._id) &&
      (
        (user.firstName && user.firstName.toLowerCase().includes(materialSupplierPersonSearch.toLowerCase())) ||
        (user.lastName && user.lastName.toLowerCase().includes(materialSupplierPersonSearch.toLowerCase())) ||
        (user.companyName && user.companyName.toLowerCase().includes(materialSupplierPersonSearch.toLowerCase())) ||
        (user.supplier && typeof user.supplier === 'object' && user.supplier.companyName && user.supplier.companyName.toLowerCase().includes(materialSupplierPersonSearch.toLowerCase()))
      )
    ).slice(0, 10);
  }, [materialSupplierUsers, materialSupplierPersonSearch, materialSuppliers]);

  const handleSelectMaterialSupplierPerson = (person) => {
    setMaterialSuppliers(prev => [...prev, person]);
    setMaterialSupplierPersonSearch('');
    setShowMaterialSupplierPersonDropdown(false);
  };

  const handleRemoveMaterialSupplierPerson = (personId) => {
    setMaterialSuppliers(prev => prev.filter(p => p._id !== personId));
  };

  // Hjälpfunktion för att hämta bolagsnamn
  const getCompanyName = (person) => {
    if (person.supplier && typeof person.supplier === 'object' && person.supplier.companyName) {
      return person.supplier.companyName;
    }
    return '';
  };

  // Hjälpfunktion för att hämta leverantörstyp
  const getSupplierType = (person) => {
    if (person.supplier && typeof person.supplier === 'object' && person.supplier.supplierType) {
      return person.supplier.supplierType;
    }
    return '';
  };

  // Uppdatera formulärfälten för att visa felmeddelanden
  return (
    <div className="p-6">
      {/* Tabs högst upp */}
      <div className="flex border-b mb-6">
        <button
          type="button"
          className={`px-4 py-2 font-medium focus:outline-none transition-colors duration-200 ${activeTab === 'project' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-primary'}`}
          onClick={() => handleTabChange('project')}
          aria-label="Projektinformation"
          tabIndex={0}
        >
          Projektinformation
        </button>
        <button
          type="button"
          className={`ml-4 px-4 py-2 font-medium focus:outline-none transition-colors duration-200 ${activeTab === 'team' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-primary'}`}
          onClick={() => handleTabChange('team')}
          aria-label="Team"
          tabIndex={0}
        >
          Team
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Projektinformation-tabben */}
        {activeTab === 'project' && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projekt ID *
              </label>
              <input
                type="text"
                name="projectId"
                value={values.projectId}
                onChange={handleChange}
                required
                placeholder="#12345"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.projectId ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.projectId && (
                <p className="mt-1 text-sm text-red-600">{errors.projectId}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projektbeskrivning
              </label>
              <input
                type="text"
                name="projectDescription"
                value={values.projectDescription}
                onChange={handleChange}
                placeholder="T.ex. Etapp 1, Övervåning"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">Valfritt beskrivande namn för projektet</p>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Projekttyp *
              </label>
              <select
                name="projectType"
                value={values.projectType}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="Renovering">Renovering</option>
                <option value="Tillbyggnad">Tillbyggnad</option>
                <option value="Taklyft">Taklyft</option>
                <option value="Källare">Källare</option>
                <option value="Övrigt">Övrigt</option>
              </select>
              {errors.projectType && (
                <p className="mt-1 text-sm text-red-600">{errors.projectType}</p>
              )}
            </div>
            <FormInput
              label="Adress"
              name="address"
              value={values.address}
              onChange={handleChange}
              required
              placeholder="Ange adress"
              error={errors.address}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postnummer *
              </label>
              <input
                type="text"
                name="postCode"
                value={values.postCode}
                onChange={handleChange}
                required
                placeholder="123 45"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.postCode ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.postCode && (
                <p className="mt-1 text-sm text-red-600">{errors.postCode}</p>
              )}
            </div>
            <FormInput
              label="Postort"
              name="city"
              value={values.city}
              onChange={handleChange}
              required
              placeholder="Ange postort"
              error={errors.city}
            />
            {/* Fastighetsbeteckning */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fastighetsbeteckning
              </label>
              <input
                type="text"
                name="propertyDesignation"
                value={values.propertyDesignation}
                onChange={handleChange}
                placeholder="T.ex. Faddergåvan 10, Södermalm 1:5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Krävs för ROT-avdrag. Hittas i fastighetsregistret.
              </p>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Kund *
              </label>
              <div className="relative customer-search-main">
                <input
                  type="text"
                  value={mainCustomerSearch}
                  onChange={(e) => {
                    setMainCustomerSearch(e.target.value);
                    setShowMainCustomerDropdown(true);
                  }}
                  onFocus={() => setShowMainCustomerDropdown(true)}
                  placeholder="Sök kund..."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    errors.customer ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                
                {/* Visa felmeddelande */}
                {errors.customer && (
                  <p className="mt-1 text-sm text-red-600">{errors.customer}</p>
                )}
                
                {/* Visa vald kund */}
                {selectedCustomer && values.customer && (
                  <div className="mt-2 bg-gray-50 px-3 py-2 rounded-lg flex items-center justify-between">
                    <span title={selectedCustomer.email}>{selectedCustomer.firstName} {selectedCustomer.lastName}</span>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-red-600"
                      onClick={() => {
                        setValues(prev => ({ ...prev, customer: '' }));
                        setSelectedCustomer(null);
                        setMainCustomerSearch('');
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                
                {/* Dropdown med sökresultat */}
                {showMainCustomerDropdown && mainCustomerSearch && !selectedCustomer && (
                  <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                    {filteredMainCustomers.length > 0 ? (
                      filteredMainCustomers.map(user => (
                        <div
                          key={`main-customer-${user._id}`}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelectMainCustomer(user)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium flex items-center">
                              {user.firstName} {user.lastName}
                              {!isCustomerAssociatedWithProjects(user._id) && 
                                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Ny</span>
                              }
                            </span>
                            <span className="text-xs text-gray-500">{user.email}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        Inga träffar
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Ytterligare kunder */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ytterligare kunder
              </label>
              <div className="relative customer-search-additional">
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  placeholder="Sök kund..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                
                {/* Dropdown med sökresultat */}
                {showCustomerDropdown && customerSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                    {filteredCustomers.length > 0 ? (
                      filteredCustomers.map(user => (
                        <div
                          key={`additional-customer-${user._id}`}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelectAdditionalCustomer(user)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium flex items-center">
                              {user.firstName} {user.lastName}
                              {!isCustomerAssociatedWithProjects(user._id) && 
                                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Ny</span>
                              }
                            </span>
                            <span className="text-xs text-gray-500">{user.email}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        Inga träffar
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Lista över valda ytterligare kunder */}
              <div className="mt-2 space-y-1">
                {values.additionalCustomers.length > 0 ? (
                  values.additionalCustomers.map(customerId => {
                    const customer = users.find(u => u._id === customerId);
                    return customer ? (
                      <div key={customerId} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                        <span title={customer.email}>{customer.firstName} {customer.lastName}</span>
                        <button
                          type="button"
                          className="text-gray-500 hover:text-red-600"
                          onClick={() => handleRemoveAdditionalCustomer(customerId)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : null;
                  })
                ) : (
                  <p className="text-gray-500 text-sm mt-1">Inga ytterligare kunder tillagda</p>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Team-tabben */}
        {activeTab === 'team' && (
          <div className="space-y-5">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Byggkonsult
              </label>
              <select
                name="consultant"
                value={values.consultant}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Välj byggkonsult</option>
                {values.consultants && values.consultants.map ? values.consultants.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                )) : null}
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Inredningsarkitekt
              </label>
              <select
                name="architect"
                value={values.architect}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Välj inredningsarkitekt</option>
                {values.architects && values.architects.map ? values.architects.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                )) : null}
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Byggföretag
              </label>
              <select
                name="builder"
                value={values.builder}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Välj byggföretag</option>
                {builders && builders.map ? builders.map(builder => (
                  <option key={builder._id} value={builder._id}>
                    {builder.companyName}
                  </option>
                )) : null}
              </select>
            </div>
            {/* Visa kontaktperson-dropdown bara om byggföretag är valt och det finns mer än en kontaktperson */}
            {values.builder && filteredBuilderContacts.length !== 1 && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Kontaktperson
                </label>
                <select
                  name="builderContactPerson"
                  value={values.builderContactPerson}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Välj kontaktperson</option>
                  {filteredBuilderContacts && filteredBuilderContacts.map ? filteredBuilderContacts.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </option>
                  )) : null}
                </select>
                {filteredBuilderContacts.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">Välj en kontaktperson för detta byggföretag</p>
                )}
              </div>
            )}
            
            {values.builder && filteredBuilderContacts.length === 1 && (
              <div className="rounded-lg bg-blue-50 p-3 text-sm">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-blue-700">
                      Kontaktperson: <span className="font-semibold">{filteredBuilderContacts[0].firstName} {filteredBuilderContacts[0].lastName}</span> 
                      <span className="block text-xs mt-1">Det finns bara en kontaktperson för detta byggföretag.</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {values.builder && filteredBuilderContacts.length === 0 && (
              <p className="text-amber-500 mt-1 text-sm">Det finns inga kontaktpersoner för det valda byggföretaget</p>
            )}

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Underleverantörer</label>
              <div className="relative subcontractor-search">
                <input
                  type="text"
                  placeholder="Sök underleverantör..."
                  value={subcontractorSearch}
                  onChange={(e) => {
                    setSubcontractorSearch(e.target.value);
                    setShowSubcontractorDropdown(true);
                  }}
                  onFocus={() => setShowSubcontractorDropdown(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                
                {/* Dropdown med sökresultat för UE */}
                {showSubcontractorDropdown && subcontractorSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                    {filteredSubcontractors.length > 0 ? (
                      filteredSubcontractors.map(ue => (
                        <div
                          key={`subcontractor-${ue._id}`}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelectSubcontractor(ue)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{ue.firstName} {ue.lastName}</span>
                            <span className="text-xs text-gray-500">
                              {ue.companyName}
                              {ue.contractorType && ` (${ue.contractorType})`}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        Inga träffar
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Lista över valda UE */}
              <div className="mt-2 space-y-1">
                {values.selectedSubcontractors.length > 0 && values.subcontractors && Array.isArray(values.subcontractors) ? (
                  values.selectedSubcontractors.map(subcontractorId => {
                    const subcontractor = values.subcontractors.find(ue => ue && ue._id === subcontractorId);
                    return subcontractor ? (
                      <div key={subcontractorId} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                        <div>
                          <span className="font-medium">{subcontractor.firstName} {subcontractor.lastName}</span>
                          <span className="text-xs text-gray-500 block">
                            {subcontractor.companyName}
                            {subcontractor.contractorType && ` (${subcontractor.contractorType})`}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="text-gray-500 hover:text-red-600"
                          onClick={() => handleRemoveSubcontractor(subcontractorId)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : null;
                  })
                ) : (
                  <p className="text-gray-500 text-sm mt-1">Inga underleverantörer tillagda</p>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Materialleverantörer */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Materialleverantörer</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Sök materialleverantör (namn eller företag)..."
              value={materialSupplierPersonSearch}
              onChange={e => {
                setMaterialSupplierPersonSearch(e.target.value);
                setShowMaterialSupplierPersonDropdown(true);
              }}
              onFocus={() => setShowMaterialSupplierPersonDropdown(true)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
            />
            {showMaterialSupplierPersonDropdown && materialSupplierPersonSearch && (
              <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                {filteredMaterialSupplierPersons.length > 0 ? (
                  filteredMaterialSupplierPersons.map(person => {
                    const company = getCompanyName(person);
                    const supplierType = getSupplierType(person);
                    return (
                      <div
                        key={person._id}
                        className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleSelectMaterialSupplierPerson(person)}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{person.firstName} {person.lastName}</span>
                          <span className="text-xs text-gray-500">
                            {company}
                            {supplierType && ` (${supplierType})`}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-4 py-2 text-gray-500">Inga träffar</div>
                )}
              </div>
            )}
          </div>
          {/* Lista över valda materialleverantörer */}
          <div className="mt-2 space-y-1">
            {materialSuppliers.length > 0 ? (
              materialSuppliers.filter(person => person && (person.firstName || person.lastName)).map(person => {
                const company = getCompanyName(person);
                const supplierType = getSupplierType(person);
                return (
                  <div key={person._id} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                    <div>
                      <span className="font-medium">{person.firstName} {person.lastName}</span>
                      <span className="text-xs text-gray-500 block">
                        {company}
                        {supplierType && ` (${supplierType})`}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-red-600"
                      onClick={() => handleRemoveMaterialSupplierPerson(person._id)}
                      aria-label={`Ta bort ${person.firstName} ${person.lastName}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-sm mt-1">Inga materialleverantörer tillagda</p>
            )}
          </div>
        </div>
        {/* Knappar längst ner */}
        <div className="flex space-x-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-500 text-white p-2 rounded hover:bg-gray-600 transition-colors duration-200"
          >
            Avbryt
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-leadme-pink text-white p-2 rounded hover:bg-leadme-pink-dark disabled:bg-leadme-pink-light transition-colors duration-200"
          >
            {isLoading ? 'Skapar...' : 'Skapa projekt'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddProject;
